import type { components } from '@connected/api-client/api.generated';
import type { ElectricalProjectInspectionKind } from '@connected/elektra-sdk/validators/electrical-project';

import { api } from '@connected/api-client';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import { Trash2 } from 'lucide-react';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

type MeasurementDeviceSelectionTableProps = {
    devices: components['schemas']['ListElectricalProjectMeasurementDeviceQueryResultDevice'][];
    inspectionKinds: ElectricalProjectInspectionKind[];
    projectVersionId: string;
    onRemove: (deviceId: string) => void;
};

export function MeasurementDeviceSelectionTable({ devices, inspectionKinds, projectVersionId, onRemove }: MeasurementDeviceSelectionTableProps) {
    const patchMutation = api.useMutation('patch', '/api/v2/electrical/projects/measurementdevices/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            if (!body) {
                throw new Error('Body is required');
            }

            const toggleKey = `${body.ElectricalProjectMeasurementDeviceId}-${body.AddInspectionKind || body.RemoveInspectionKind}`;

            const queryOptions = elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({
                electricalProjectVersionId: projectVersionId,
            });
            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'];

            const newData: components['schemas']['ListElectricalProjectMeasurementDeviceQueryResult'] = {
                Devices: previousData?.Devices?.map((device) => {
                    if (device.ElectricalProjectMeasurementDeviceId === body.ElectricalProjectMeasurementDeviceId) {
                        let updatedInspectionKinds = [...(device.InspectionKinds || [])];

                        if (body.AddInspectionKind) {
                            updatedInspectionKinds.push(body.AddInspectionKind);
                        }

                        if (body.RemoveInspectionKind) {
                            updatedInspectionKinds = updatedInspectionKinds.filter(
                                kind => kind !== body.RemoveInspectionKind,
                            );
                        }

                        return {
                            ...device,
                            InspectionKinds: updatedInspectionKinds,
                        };
                    }
                    return device;
                }),
            };

            logger.debug({
                message: 'onMutate(measurementDevicePatch)',
                path: '/api/v2/electrical/projects/measurementdevices/patch',
                measurementDevice: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions, toggleKey };
        },
        onError: async (_error, _variables, context: any) => {
            if (context?.queryOptions && context?.previousData) {
                queryClient.setQueryData(context.queryOptions.queryKey, context.previousData);
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.information.selectedMeasurementDevicesListOptions({
                    electricalProjectVersionId: projectVersionId,
                }),
            );
        },
        scope: {
            id: projectVersionId,
        },
    });

    function handleToggle(deviceId: string, inspectionKind: components['schemas']['ElectricalInspectionKind'], currentlySelected: boolean) {
        patchMutation.mutate({
            body: {
                ElectricalProjectMeasurementDeviceId: deviceId,
                ...(currentlySelected
                    ? { RemoveInspectionKind: inspectionKind }
                    : { AddInspectionKind: inspectionKind }),
            },
        });
    }

    if (devices.length === 0) {
        return (
            <div className="text-center text-muted-foreground py-8">
                Geen meetinstrumenten toegevoegd
            </div>
        );
    }

    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[300px]">Meetinstrument</TableHead>
                        {inspectionKinds.map(inspectionKind => (
                            <TableHead key={inspectionKind} className="text-center">
                                {/* TODO: Translate */}
                                {inspectionKind}
                            </TableHead>
                        ))}
                        <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {devices.map(device => (
                        <TableRow key={`${device.ElectricalProjectMeasurementDeviceId}-row`}>
                            <TableCell>
                                <div className="flex flex-col">
                                    <span className="font-medium">{device.Name}</span>
                                    {(device.StartDate || device.EndDate) && (
                                        <span className="text-sm text-muted-foreground">
                                            {device.StartDate
                                                ? new Date(device.StartDate).toLocaleDateString()
                                                : 'N/A'}
                                            {' - '}
                                            {device.EndDate
                                                ? new Date(device.EndDate).toLocaleDateString()
                                                : 'N/A'}
                                        </span>
                                    )}
                                </div>
                            </TableCell>
                            {inspectionKinds.map((inspectionKind) => {
                                const isSelected = device.InspectionKinds?.includes(inspectionKind) ?? false;

                                return (
                                    <TableCell key={`${device.ElectricalProjectMeasurementDeviceId}-${inspectionKind}-cell`} className="text-center">
                                        <div className="flex justify-center">
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() =>
                                                    handleToggle(
                                                        device.ElectricalProjectMeasurementDeviceId!,
                                                        inspectionKind,
                                                        isSelected,
                                                    )}
                                            />
                                        </div>
                                    </TableCell>
                                );
                            })}
                            <TableCell className={inspectionKinds.length <= 1 ? 'text-right' : ''}>
                                <div className={inspectionKinds.length <= 1 ? 'flex justify-end' : ''}>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onRemove(device.ElectricalProjectMeasurementDeviceId!)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
